/**
 * Reliability score — a behaviour-derived trust signal (Phase A2). Pure so it
 * can be unit-tested and shared between the Worker (computes the number) and
 * the Mini App (derives the calm green/amber label).
 *
 * Deliberately gentle (autonomy pillar: "not hostile"). Built from data we
 * already store: completed rides (positive) and recent cancellations
 * (negative). No no-show/confirmation inputs yet — those arrive with trip
 * confirmation (A3) and can be folded in then.
 */

export interface ReliabilityInput {
  ridesCompleted: number;
  /** users.cancellation_count_30d — cancellations in the trailing 30 days. */
  cancellationCount30d: number;
}

const BASE = 80;
const COMPLETED_CAP = 10;

/** 0–100. Everyone starts at 80; rides nudge up, recent cancels nudge down. */
export function computeReliability(i: ReliabilityInput): number {
  const score =
    BASE + Math.min(Math.max(i.ridesCompleted, 0), COMPLETED_CAP) * 2 - Math.max(i.cancellationCount30d, 0) * 8;
  return Math.max(0, Math.min(100, Math.round(score)));
}

export type ReliabilityLevel = 'reliable' | 'building' | 'low';

/**
 * Three-state signal for display. New drivers (<3 completed) read as
 * "building" rather than "reliable" no matter the raw number. "low" is never
 * shown as a hostile badge on cards — callers treat it as "show nothing".
 */
export function reliabilityLevel(input: { score: number; ridesCompleted: number }): ReliabilityLevel {
  if (input.ridesCompleted < 3) return 'building';
  if (input.score >= 80) return 'reliable';
  if (input.score >= 60) return 'building';
  return 'low';
}
